import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Calculator, Save, Scale } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { CoberturaMeses } from "@/components/CoberturaMeses";
import {
  calcularSombra,
  calendarioPotencia,
  getB2bPendientes,
  getContratos,
  getSombras,
  guardarSombra,
  sombraCel,
  sombraIrec,
  sombraPotencia,
  type CelFactura,
  type FacturacionResultado,
  type PotenciaFactura,
} from "@/lib/api";
import { ResumenFactura } from "@/pages/FacturasPage";

const mxn = (n: number, moneda = "MXN") =>
  new Intl.NumberFormat("es-MX", { style: "currency", currency: moneda === "USD" ? "USD" : "MXN" }).format(n);
const money = mxn;
const num = (n: number, d = 2) =>
  new Intl.NumberFormat("es-MX", { minimumFractionDigits: d, maximumFractionDigits: d }).format(n);

const addDays = (iso: string, n: number) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const clampHasta = (inicio: string, fin: string | null) => {
  const def = addDays(inicio, 14);
  return fin && def > fin ? fin : def;
};

type FacturaInput = { ce_q: number; ctreg_q: number; cf_q: number };

// Producto del radio (como en /facturacion): energía valida con el motor
// heat-rate; potencia por parcialidad; CEL e i-REC por certificados × precio.
const PRODUCTOS = ["Energía", "Potencia", "CEL", "i-REC"] as const;
type Producto = (typeof PRODUCTOS)[number];

export function SombraPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const contratosQ = useQuery({ queryKey: ["contratos"], queryFn: getContratos });
  // Ejercicio Sombra: contratos donde MPP RECIBE la TBFin (comprador). El radio
  // de producto y el checkbox de vigencia filtran el combo como en /facturacion.
  const [producto, setProducto] = useState<Producto>("Energía");
  const [soloVigentes, setSoloVigentes] = useState(true);
  const hoyIso = new Date().toISOString().slice(0, 10);
  const vigenteHoy = (c: { fecha_fin?: string | null }) => !c.fecha_fin || c.fecha_fin >= hoyIso;

  const compradores = (contratosQ.data?.items ?? []).filter((c) => c.rol_mpp === "comprador");
  const delProducto = compradores.filter((c) => (c.productos ?? []).includes(producto));
  const contratos = soloVigentes ? delProducto.filter(vigenteHoy) : delProducto;
  // Los radios se habilitan mirando TODOS los contratos (sin filtro de vigencia)
  // para que un producto sin vigentes no quede inalcanzable.
  const hayDe = (p: Producto) => compradores.some((c) => (c.productos ?? []).includes(p));
  const ocultosPorVigencia = delProducto.length - contratos.length;
  // Solo Energía se liquida con el motor heat-rate del ejercicio sombra.
  const esEnergia = producto === "Energía";

  const [contrato, setContrato] = useState("");
  const [desde, setDesde] = useState("2026-05-16");
  const [hasta, setHasta] = useState("2026-05-31");
  // Potencia y CEL se validan por MES (y por parcialidad en potencia).
  const [ym, setYm] = useState(() => new Date().toISOString().slice(0, 7));
  const [nPago, setNPago] = useState<number | null>(null);
  const [resPot, setResPot] = useState<PotenciaFactura | null>(null);
  const [resCel, setResCel] = useState<CelFactura | null>(null);
  const [facImporte, setFacImporte] = useState<number>(0);
  const [res, setRes] = useState<FacturacionResultado | null>(null);
  const [fac, setFac] = useState<FacturaInput | null>(null);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Si el contrato elegido salió de la lista (cambió el producto o el filtro de
  // vigencia), cae al primero disponible.
  const enLista = contratos.some((c) => String(c.id_contrato) === contrato);
  const contratoSel = enLista
    ? contrato
    : contratos[0] ? String(contratos[0].id_contrato) : "";
  const sel = contratos.find((c) => String(c.id_contrato) === contratoSel);

  // Ejercicios ya guardados: cada día solo puede pertenecer a UN ejercicio por
  // contrato, así que un rango que traslape con uno guardado no se puede
  // volver a ejercer (el backend también lo rechaza con 409).
  const sombrasQ = useQuery({ queryKey: ["sombras"], queryFn: getSombras });
  const sombrasContrato = (sombrasQ.data ?? []).filter(
    (it) => String(it.id_contrato) === contratoSel,
  );
  // Meses con TBFin liquidada SIN sombra (bolita roja en el mes del semáforo).
  const pendQ = useQuery({
    queryKey: ["b2b-pendientes"],
    queryFn: getB2bPendientes,
    staleTime: 5 * 60 * 1000,
  });
  const mesesPendientes =
    pendQ.data?.sombra.find((p) => p.folio === sel?.folio)?.meses_captura ?? [];
  // El traslape se evalúa contra el MISMO producto y el periodo activo:
  // energía usa el rango desde–hasta; potencia/CEL, el mes completo.
  const mesIni = `${ym}-01`;
  const mesFin = (() => {
    const [a, m] = ym.split("-").map(Number);
    const d = new Date(a, m, 0);
    return `${ym}-${String(d.getDate()).padStart(2, "0")}`;
  })();
  const perIni = producto === "Energía" ? desde : mesIni;
  const perFin = producto === "Energía" ? hasta : mesFin;
  const traslapes = sombrasContrato.filter(
    (it) =>
      (it.producto ?? "Energía") === producto && it.desde <= perFin && it.hasta >= perIni,
  );

  // Al cambiar de contrato, ajusta el rango por defecto a su vigencia (los date
  // pickers ya quedan limitados con min/max a [fecha_inicio, fecha_fin]).
  useEffect(() => {
    if (!sel?.fecha_inicio) return;
    setDesde(sel.fecha_inicio);
    setHasta(clampHasta(sel.fecha_inicio, sel.fecha_fin));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratoSel, sel?.fecha_inicio, sel?.fecha_fin]);

  // Calendario de pagos (potencia): decide si se elige parcialidad o mes.
  const calPotQ = useQuery({
    queryKey: ["calendario-potencia", contratoSel],
    queryFn: () => calendarioPotencia(contratoSel),
    enabled: !!contratoSel && producto === "Potencia",
    retry: false,
  });
  const calPot = producto === "Potencia" ? calPotQ.data : undefined;
  const porParcialidad = calPot?.modo === "explicito";
  useEffect(() => {
    const ps = calPot?.parcialidades ?? [];
    setNPago(ps.length ? ps[0].n : null);
  }, [calPot]);

  const limpiarResultados = () => {
    setRes(null);
    setFac(null);
    setResPot(null);
    setResCel(null);
  };

  const calcular = async () => {
    setLoading(true);
    setError(null);
    setMsg(null);
    try {
      limpiarResultados();
      if (producto === "Potencia") {
        const r = await sombraPotencia(
          contratoSel,
          porParcialidad && nPago != null ? { n_pago: nPago } : { ym },
        );
        setResPot(r);
        setFacImporte(r.importe); // se pre-llena con el modelo; el usuario ajusta
      } else if (producto === "CEL" || producto === "i-REC") {
        const r = await (producto === "CEL" ? sombraCel : sombraIrec)(contratoSel, ym);
        setResCel(r);
        setFacImporte(r.importe);
      } else {
        const r = await calcularSombra(contratoSel, desde, hasta);
        setRes(r);
        // Pre-llena la columna "Factura recibida" con el modelo; el usuario ajusta lo que difiera.
        setFac({ ce_q: r.ce_q, ctreg_q: r.ctreg_q, cf_q: r.cf_q });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al calcular");
      limpiarResultados();
    } finally {
      setLoading(false);
    }
  };

  // Recalcula automáticamente al cambiar contrato o rango de fechas, para que el
  // comparativo y la memoria de cálculo siempre reflejen lo seleccionado (antes
  // se quedaban con el rango anterior hasta volver a presionar "Calcular").
  // Debounce de 400ms para no disparar en cada cambio intermedio.
  useEffect(() => {
    if (!contratoSel) return;
    if (esEnergia && (!desde || !hasta)) return;
    if (!esEnergia && !ym) return;
    const t = setTimeout(() => {
      void calcular();
    }, 400);
    return () => clearTimeout(t);
    // calcular se recrea cada render; solo nos interesan estos disparadores.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratoSel, desde, hasta, esEnergia, producto, ym, nPago, porParcialidad]);

  const guardar = async () => {
    setGuardando(true);
    setMsg(null);
    setError(null);
    try {
      const it = await guardarSombra(
        esEnergia
          ? {
              contrato: contratoSel,
              producto,
              desde,
              hasta,
              fac_ce_q: fac?.ce_q ?? 0,
              fac_ctreg_q: fac?.ctreg_q ?? 0,
              fac_cf_q: fac?.cf_q ?? 0,
            }
          : {
              contrato: contratoSel,
              producto,
              ym: producto === "Potencia" ? (resPot?.ym ?? ym) : ym,
              ...(producto === "Potencia" && porParcialidad && nPago != null
                ? { n_pago: nPago }
                : {}),
              fac_importe: facImporte,
            },
      );
      // La lista de guardados se cachea: sin invalidar, al volver a
      // /sombra/guardados el ejercicio recién creado no aparecía.
      await qc.invalidateQueries({ queryKey: ["sombras"] });
      navigate(`/sombra/guardados/${it.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al guardar el ejercicio");
      setGuardando(false);
    }
  };

  // Totales de la columna "Factura recibida" (derivados de los 3 cargos capturados).
  const facSubtotal = fac ? fac.ce_q + fac.ctreg_q + fac.cf_q : 0;
  const facIva = res ? facSubtotal * res.iva_pct : 0;
  const facTotal = facSubtotal + facIva;

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Ejercicio Sombra</h1>
          <p className="text-sm text-slate-500 mt-1">
            Para contratos donde MPP <b>recibe</b> la TBFin: calcula el <b>modelo</b> de lo que la
            contraparte debería cobrarte y <b>valida</b> la factura recibida.
          </p>
        </div>
        <Link to="/sombra/guardados" className="text-blue-600 hover:underline text-sm whitespace-nowrap">
          Ver guardados →
        </Link>
      </div>

      <div className="bg-white border rounded-lg p-4 space-y-3">
        {/* Producto + solo vigentes: filtran el combo de contratos (como en /facturacion) */}
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-xs font-medium text-slate-600">Producto</span>
          {PRODUCTOS.map((p) => {
            const disponible = hayDe(p);
            return (
              <label
                key={p}
                className={`inline-flex items-center gap-1.5 text-sm ${
                  disponible ? "cursor-pointer text-slate-700" : "text-slate-300 cursor-not-allowed"
                }`}
                title={disponible ? undefined : `No hay contratos de compra con ${p}`}
              >
                <input
                  type="radio"
                  name="producto-sombra"
                  checked={producto === p}
                  disabled={!disponible}
                  onChange={() => {
                    setProducto(p);
                    setRes(null);
                    setFac(null);
                    setError(null);
                    setMsg(null);
                  }}
                  className="w-4 h-4 accent-blue-600"
                />
                {p}
              </label>
            );
          })}
          <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={soloVigentes}
              onChange={(e) => setSoloVigentes(e.target.checked)}
              className="w-4 h-4 accent-blue-600 cursor-pointer"
            />
            Mostrar solo contratos vigentes
          </label>
          <span className="text-[11px] text-slate-400">
            {contratos.length} contrato{contratos.length === 1 ? "" : "s"} de compra con {producto}
            {soloVigentes && ocultosPorVigencia > 0 && ` · ${ocultosPorVigencia} oculto${
              ocultosPorVigencia === 1 ? "" : "s"} por vigencia`}
          </span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Contrato (MPP comprador)</span>
          <select
            className="mt-1 border rounded px-2 py-1.5 text-sm w-72"
            value={contratoSel}
            onChange={(e) => setContrato(e.target.value)}
          >
            {contratos.map((c) => (
              <option key={c.id_contrato} value={c.id_contrato}>
                {c.folio} — {c.contraparte}
              </option>
            ))}
          </select>
        </label>
        {esEnergia ? (
          <>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Desde</span>
              <input type="date" className="mt-1 border rounded px-2 py-1.5 text-sm"
                value={desde}
                onChange={(e) => {
                  const v = e.target.value;
                  setDesde(v);
                  // "Hasta" arranca en la misma fecha: el usuario parte de ahí
                  // (y nunca se queda con un rango invertido)
                  if (v) setHasta(v);
                }}
                min={sel?.fecha_inicio ?? undefined} max={sel?.fecha_fin ?? undefined} />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Hasta</span>
              <input type="date" className="mt-1 border rounded px-2 py-1.5 text-sm"
                value={hasta} onChange={(e) => setHasta(e.target.value)}
                min={desde || (sel?.fecha_inicio ?? undefined)}
                max={sel?.fecha_fin ?? undefined} />
            </label>
          </>
        ) : (
          <>
            {producto === "Potencia" && porParcialidad ? (
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Parcialidad</span>
                <select
                  className="mt-1 border rounded px-2 py-1.5 text-sm"
                  value={nPago ?? ""}
                  onChange={(e) => setNPago(Number(e.target.value))}
                >
                  {(calPot?.parcialidades ?? []).map((p) => (
                    <option key={p.n} value={p.n}>
                      Pago {p.n} · {p.ym} · {p.tipo}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Periodo (Año-Mes)</span>
                <input
                  type="month"
                  className="mt-1 border rounded px-2 py-1.5 text-sm"
                  value={ym}
                  onChange={(e) => e.target.value && setYm(e.target.value)}
                  min={sel?.fecha_inicio?.slice(0, 7)}
                  max={sel?.fecha_fin?.slice(0, 7)}
                />
              </label>
            )}
          </>
        )}
        <button
          type="button"
          onClick={calcular}
          disabled={loading || !contratoSel}
          className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Calculator className="w-4 h-4" /> {loading ? "Calculando…" : "Calcular modelo"}
        </button>
        {sel?.fecha_inicio && (
          <span className="text-xs text-slate-400 pb-2">
            Vigencia: {sel.fecha_inicio} a {sel.fecha_fin ?? "—"}
          </span>
        )}

        {/* Semáforo de cobertura mensual: cuenta los ejercicios del producto activo */}
        {sel && (
          <CoberturaMeses
            vigIni={sel.fecha_inicio}
            vigFin={sel.fecha_fin}
            periodos={sombrasContrato.filter((s) => (s.producto ?? "Energía") === producto)}
            storageKey="sombra-cal-anio"
            docLabel={`ejercicios sombra de ${producto}`}
            pendientes={producto === "Energía" ? mesesPendientes : undefined}
          />
        )}
        </div>
        {contratos.length === 0 && (
          <div className="text-sm text-slate-500 bg-slate-50 border rounded p-3">
            {ocultosPorVigencia > 0 ? (
              <>
                No hay contratos de compra de <b>{producto}</b> con vigencia abierta hoy
                ({hoyIso}). Desmarca <b>“Mostrar solo contratos vigentes”</b> para ver los{" "}
                {ocultosPorVigencia} contrato{ocultosPorVigencia === 1 ? "" : "s"} ya
                vencido{ocultosPorVigencia === 1 ? "" : "s"}.
              </>
            ) : (
              <>No hay contratos de compra con {producto}.</>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>
      )}

      {traslapes.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">
          ⚠ El periodo {perIni} a {perFin} <b>traslapa con ejercicio{traslapes.length === 1 ? "" : "s"} sombra de {producto} ya guardado{traslapes.length === 1 ? "" : "s"}</b> de este contrato:{" "}
          {traslapes.map((t, i) => (
            <span key={t.id}>
              {i > 0 && ", "}
              <Link to={`/sombra/guardados/${t.id}`} className="underline hover:text-amber-950">
                #{t.id} ({t.desde} a {t.hasta})
              </Link>
            </span>
          ))}
          . Cada día solo puede pertenecer a un ejercicio — para rehacerlo, bórralo primero en{" "}
          <Link to="/sombra/guardados" className="underline hover:text-amber-950">
            guardados
          </Link>{" "}
          (requiere el permiso “Borrar ejercicios sombra”).
        </div>
      )}

      {/* Comparativo de un solo concepto (Potencia / CEL) */}
      {(resPot || resCel) && (
        <ComparativoUnico
          etiqueta={
            resPot
              ? `${resPot.descripcion} · ${num(resPot.cantidad, 4)} MAW × ${money(
                  resPot.precio_unitario,
                  resPot.moneda,
                )}`
              : `${resCel!.descripcion} · ${num(resCel!.cel, 0)} ${resCel!.producto ?? "CEL"}s × ${money(
                  resCel!.precio_unitario,
                  resCel!.moneda,
                )}`
          }
          encabezado={`${(resPot ?? resCel)!.folio} — ${(resPot ?? resCel)!.contraparte} · ${
            (resPot ?? resCel)!.ym
          }`}
          modelo={(resPot ?? resCel)!.importe}
          ivaPct={(resPot ?? resCel)!.iva_pct}
          moneda={(resPot ?? resCel)!.moneda}
          facturado={facImporte}
          onFactura={setFacImporte}
          advertencias={(resPot ?? resCel)!.advertencias}
          guardando={guardando}
          bloqueado={traslapes.length > 0}
          onGuardar={guardar}
        />
      )}

      {res && fac && (
        <>
          {/* Comparativo: Factura recibida (editable) vs Modelo */}
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b bg-slate-50 flex items-center gap-2">
              <Scale className="w-4 h-4 text-slate-500" />
              <span className="font-medium text-slate-800">Comparativo: factura recibida vs modelo</span>
              <span className="text-xs text-slate-500">
                {res.folio} — {res.contraparte} · {res.desde} a {res.hasta}
              </span>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 border-b">
                  <th className="px-4 py-2 text-left">Concepto</th>
                  <th className="px-4 py-2 text-right">Factura recibida</th>
                  <th className="px-4 py-2 text-right">Modelo (calculado)</th>
                  <th className="px-4 py-2 text-right">Diferencia</th>
                  <th className="px-4 py-2 text-right">% Dif.</th>
                </tr>
              </thead>
              <tbody>
                <FilaCmp etiqueta="Cargo por Energía (CE_q)" modelo={res.ce_q} factura={fac.ce_q} moneda={res.moneda}
                  onChange={(v) => setFac({ ...fac, ce_q: v })} />
                <FilaCmp etiqueta="Tarifas Reguladas (CTreg_q)" modelo={res.ctreg_q} factura={fac.ctreg_q} moneda={res.moneda}
                  onChange={(v) => setFac({ ...fac, ctreg_q: v })} />
                {res.moneda !== "USD" && (
                  <FilaCmp etiqueta="Cargo Fijo Quincenal (CF_q)" modelo={res.cf_q} factura={fac.cf_q} moneda={res.moneda}
                    onChange={(v) => setFac({ ...fac, cf_q: v })} />
                )}
                <FilaCmp etiqueta="Subtotal (sin IVA)" modelo={res.subtotal} factura={facSubtotal} bold moneda={res.moneda} />
                <FilaCmp etiqueta={`IVA (${(res.iva_pct * 100).toFixed(0)}%)`} modelo={res.iva} factura={facIva} moneda={res.moneda} />
                <FilaCmp etiqueta="TOTAL a pagar (con IVA)" modelo={res.total} factura={facTotal} bold total moneda={res.moneda} />
              </tbody>
            </table>
            <div className="px-4 py-2 text-xs text-slate-400 border-t">
              Edita la columna “Factura recibida” con los montos del estado de cuenta de la contraparte.
              Diferencia &gt; 0 = te cobran de más; &lt; 0 = de menos. (Subtotal/IVA/Total se calculan de los 3 cargos.)
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={guardar}
              disabled={guardando || traslapes.length > 0}
              title={
                traslapes.length > 0
                  ? "El periodo traslapa con un ejercicio ya guardado de este contrato"
                  : undefined
              }
              className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded px-4 py-2 text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" /> {guardando ? "Guardando…" : "Guardar ejercicio"}
            </button>
            {msg && <span className="text-sm text-emerald-700">{msg}</span>}
          </div>

          {/* Detalle del modelo (cómo se calculó) */}
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide pt-1">
            Soporte del modelo
          </div>
          <ResumenFactura res={res} />
        </>
      )}
    </div>
  );
}

// Comparativo de un solo concepto (Potencia / CEL): el modelo contra el
// importe de la factura recibida, con IVA y total derivados.
function ComparativoUnico({
  etiqueta,
  encabezado,
  modelo,
  ivaPct,
  moneda,
  facturado,
  onFactura,
  advertencias,
  guardando,
  bloqueado,
  onGuardar,
}: {
  etiqueta: string;
  encabezado: string;
  modelo: number;
  ivaPct: number;
  moneda: string;
  facturado: number;
  onFactura: (v: number) => void;
  advertencias: string[];
  guardando: boolean;
  bloqueado: boolean;
  onGuardar: () => void;
}) {
  const facIva = facturado * ivaPct;
  const facTotal = facturado + facIva;
  return (
    <>
      {advertencias.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm space-y-1">
          {advertencias.map((a, i) => (
            <div key={i}>⚠ {a}</div>
          ))}
        </div>
      )}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b bg-slate-50 flex flex-wrap items-center gap-2">
          <Scale className="w-4 h-4 text-slate-500" />
          <span className="font-medium text-slate-800">
            Comparativo: factura recibida vs modelo
          </span>
          <span className="text-xs text-slate-500">{encabezado}</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-slate-500 border-b">
              <th className="px-4 py-2 text-left">Concepto</th>
              <th className="px-4 py-2 text-right">Factura recibida</th>
              <th className="px-4 py-2 text-right">Modelo (calculado)</th>
              <th className="px-4 py-2 text-right">Diferencia</th>
              <th className="px-4 py-2 text-right">% Dif.</th>
            </tr>
          </thead>
          <tbody>
            <FilaCmp
              etiqueta={etiqueta}
              modelo={modelo}
              factura={facturado}
              moneda={moneda}
              onChange={onFactura}
            />
            <FilaCmp
              etiqueta={`IVA (${(ivaPct * 100).toFixed(0)}%)`}
              modelo={modelo * ivaPct}
              factura={facIva}
              moneda={moneda}
            />
            <FilaCmp
              etiqueta="TOTAL a pagar (con IVA)"
              modelo={modelo * (1 + ivaPct)}
              factura={facTotal}
              bold
              total
              moneda={moneda}
            />
          </tbody>
        </table>
        <div className="px-4 py-2 text-xs text-slate-400 border-t">
          Captura el importe (sin IVA) del CFDI que emitió la contraparte. Diferencia &gt; 0 =
          te cobran de más; &lt; 0 = de menos.
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onGuardar}
          disabled={guardando || bloqueado}
          title={bloqueado ? "El periodo ya tiene un ejercicio guardado" : undefined}
          className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded px-4 py-2 text-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save className="w-4 h-4" /> {guardando ? "Guardando…" : "Guardar ejercicio"}
        </button>
      </div>
    </>
  );
}

function FilaCmp({
  etiqueta,
  modelo,
  factura,
  onChange,
  bold,
  total,
  moneda = "MXN",
}: {
  etiqueta: string;
  modelo: number;
  factura: number;
  onChange?: (v: number) => void;
  bold?: boolean;
  total?: boolean;
  moneda?: string;
}) {
  const dif = factura - modelo;
  const pct = modelo !== 0 ? (dif / modelo) * 100 : 0;
  const coincide = Math.abs(dif) < 0.5;
  const color = coincide ? "text-emerald-700" : dif > 0 ? "text-red-700" : "text-amber-700";
  return (
    <tr className={`border-t ${total ? "border-t-2 border-slate-300 bg-emerald-50" : bold ? "bg-slate-50" : ""}`}>
      <td className={`px-4 py-2 ${bold ? "font-medium" : ""}`}>{etiqueta}</td>
      <td className="px-4 py-2 text-right">
        {onChange ? (
          <input
            type="number"
            step="0.01"
            value={factura}
            onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
            className="w-36 border rounded px-2 py-1 text-right text-sm tabular-nums"
          />
        ) : (
          <span className={`tabular-nums ${bold ? "font-medium" : ""}`}>{mxn(factura, moneda)}</span>
        )}
      </td>
      <td className={`px-4 py-2 text-right tabular-nums ${bold ? "font-medium" : ""}`}>{mxn(modelo, moneda)}</td>
      <td className={`px-4 py-2 text-right tabular-nums font-medium ${color}`}>
        {coincide ? "≈ " : dif > 0 ? "+" : ""}
        {mxn(dif, moneda)}
      </td>
      <td className={`px-4 py-2 text-right tabular-nums ${color}`}>
        {coincide ? "—" : `${pct > 0 ? "+" : ""}${pct.toFixed(2)}%`}
      </td>
    </tr>
  );
}
